'use client';
import React, { startTransition } from 'react';
import classNames from 'classnames';
import styled from '@benzinga/themetron';
import { useNearEndScroll, useRefElementSize, useScrollShadow } from '@benzinga/hooks';
import { ErrorBoundary } from '@benzinga/core-ui';
import { StyledTableRow, TableRow } from './TableRow';
import { TableHead } from './TableHead';
import { SortBy, sortRowData } from '../../libs/utils';
import { useScrollbarWidth } from '../../libs/useScrollbarWidth';
import { ColumnDef } from '../../interface';
import { TableContainer } from '../shared';
import { BaseTableProps } from '../shared/types';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface TableProps<T = unknown> extends BaseTableProps<T> {}

export const Table = <T,>({
  adPlacement,
  autoHeight = false,
  className,
  colorRowByValue = false,
  colorRowField = '',
  columnsDef,
  fitContent = false,
  gatedRowBlur = 'cell',
  gatedVisibleRowCount,
  getRowId,
  hasStockTableStyling,
  height,
  hiddenColumns,
  hiddenColumnsWhenGated,
  isGated,
  isLoading = false,
  loadingText,
  mobileHeight,
  nearEndOffsetPx,
  noDataText,
  onNearEnd,
  rowData,
  seeMoreLink,
  seeMoreText,
  sortEnabled: sortEnabledProp = true,
  tableClassName,
  title,
}: TableProps<T>) => {
  const resolvedHeight = autoHeight ? undefined : (height ?? 500);
  const [sortBy, setSortBy] = React.useState<SortBy>({});

  const ref = React.useRef<HTMLDivElement | null>(null);
  const tableWrapperRef = React.useRef<HTMLDivElement>(null);

  const { width } = useRefElementSize(ref);
  const isGreaterThan700 = width > 700;

  const scrollbarWidth = useScrollbarWidth();
  const { showEnd: showScrollShadow } = useScrollShadow(tableWrapperRef);
  useNearEndScroll(tableWrapperRef, {
    enabled: !!onNearEnd && !isLoading,
    onNearEnd,
    thresholdPx: nearEndOffsetPx,
  });

  const filteredColumnsDef = (columnsDef ?? []).filter(column => {
    const shouldBeHidden = Array.isArray(hiddenColumns) && hiddenColumns.includes(column.field);
    return !shouldBeHidden;
  });

  const handleSortChange = React.useCallback(
    (newSortByField: string, columnDef: ColumnDef) => {
      let direction: 'desc' | 'asc' = 'asc';
      startTransition(() => {
        if (sortBy.field !== newSortByField) {
          direction = 'asc';
          setSortBy({ columnDef, direction, field: newSortByField });
        } else if (sortBy?.direction === 'asc') {
          direction = 'desc';
          setSortBy({ columnDef, direction, field: newSortByField });
        } else if (sortBy?.direction === 'desc') {
          setSortBy({});
        } else {
          setSortBy({ columnDef, direction, field: newSortByField });
        }
      });
    },
    [sortBy],
  );

  const sortedRowData = React.useMemo(() => sortRowData(rowData ?? [], sortBy), [rowData, sortBy]);

  const isLastColumnSortable = !filteredColumnsDef[filteredColumnsDef.length - 1]?.sortEnabled || false;

  const getRowKey = (item: T, index: number): string => {
    if (getRowId) {
      return getRowId(item, index);
    }
    const firstColumnField = filteredColumnsDef[0]?.field;
    const fieldValue = firstColumnField ? (item as Record<string, unknown>)[firstColumnField] : null;
    const identifier = typeof fieldValue === 'string' || typeof fieldValue === 'number' ? fieldValue : index;
    return `benzinga-core-table-row-${identifier}-${index}`;
  };

  const rowIsGated = (index: number) => {
    if (!isGated) return false;
    if (gatedVisibleRowCount != null) {
      return index >= gatedVisibleRowCount;
    }
    return index > 2;
  };

  return (
    <TableContainer
      alignLastRowsRight={isLastColumnSortable}
      className={className}
      containerRef={ref}
      hasStockTableStyling={hasStockTableStyling}
      height={resolvedHeight}
      isLoading={isLoading}
      loadingText={loadingText}
      mobileHeight={mobileHeight}
      seeMoreLink={seeMoreLink}
      seeMoreText={seeMoreText}
      title={title}
    >
      <div className="benzinga-core-table-wrapper overflow-x-auto no-scrollbar" ref={tableWrapperRef}>
        <StyledTable
          $alignLastRowsRight={isLastColumnSortable}
          $hasStockTableStyling={hasStockTableStyling}
          className={classNames('benzinga-core-table relative', tableClassName, {
            'min-w-[600px]': !isGreaterThan700 && !fitContent,
            'min-w-[unset]': isGreaterThan700 && !fitContent,
            'min-w-max': fitContent,
          })}
        >
          <TableHead
            $alignLastRowsRight={isLastColumnSortable}
            $hasStockTableStyling={hasStockTableStyling}
            columnsDef={filteredColumnsDef}
            hiddenColumnsWhenGated={hiddenColumnsWhenGated}
            isGated={isGated}
            onSortChange={sortEnabledProp ? handleSortChange : undefined}
            sortBy={sortEnabledProp ? sortBy : undefined}
          />
          <StyledTBody
            $height={resolvedHeight}
            $isEmpty={!sortedRowData?.length}
            className="benzinga-core-table-tbody whitespace-nowrap w-full h-full overflow-y-auto"
          >
            {Array.isArray(sortedRowData) && sortedRowData.length ? (
              sortedRowData.map((item, index) => (
                <React.Fragment key={getRowKey(item, index)}>
                  <TableRow
                    $hasStockTableStyling={hasStockTableStyling}
                    colorRowByValue={colorRowByValue}
                    colorRowField={colorRowField}
                    columnsDef={filteredColumnsDef}
                    data={item}
                    gatedRowBlur={gatedRowBlur}
                    hiddenColumnsWhenGated={hiddenColumnsWhenGated}
                    index={index}
                    isGated={rowIsGated(index)}
                    isPayWall={isGated}
                  />
                  {adPlacement && sortedRowData.length > 3 && index === 2 && (
                    <ErrorBoundary name="stock-movers-table-ad-placement">
                      <StyledTableRow $hasStockTableStyling={hasStockTableStyling}>
                        <td colSpan={filteredColumnsDef.length}>
                          <div>{adPlacement}</div>
                        </td>
                      </StyledTableRow>
                    </ErrorBoundary>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan={filteredColumnsDef.length + 1}>
                  <div className="mt-2 text-center font-semibold">{noDataText || 'No data available to display'}</div>
                </td>
              </tr>
            )}
          </StyledTBody>
        </StyledTable>
      </div>
      <StyledShadow $right={scrollbarWidth} $visible={showScrollShadow} />
    </TableContainer>
  );
};

export const StyledTable = styled.table<{
  $alignLastRowsRight?: boolean;
  $hasStockTableStyling?: boolean;
}>`
  width: 100%;
  text-align: left;
  font-size: 12px;
  font-family: sans-serif;
  font-weight: ${({ $hasStockTableStyling }) => ($hasStockTableStyling ? 700 : `normal`)};

  tr {
    font-size: inherit;
  }

  th,
  td {
    padding: ${({ $hasStockTableStyling }) => ($hasStockTableStyling ? '0.2rem 0.5rem' : '0.35rem 0.5rem')};
  }
  td {
    &:last-of-type {
      text-align: ${({ $alignLastRowsRight }) => ($alignLastRowsRight ? 'right' : 'unset')};
    }
  }
`;

export const StyledTBody = styled.tbody<{
  $height?: number;
  $isEmpty?: boolean;
}>`
  &.benzinga-core-table-tbody,
  &.benzinga-core-virtualized-table-tbody {
    font-size: 0.75rem;
    height: ${({ $height, $isEmpty }) => ($isEmpty ? ($height ? `${$height}px` : '300px') : 'unset')};
    tr {
      @media (max-width: 800px) {
        height: 40px;
      }
    }
  }
`;

export const StyledLine = styled.div`
  flex-grow: 1;
  position: relative;
  height: 1px;

  &:before {
    content: '';
    width: 100%;
    height: 1px;
    display: block;
    background-color: #d6d6d6;
    position: absolute;
    top: 50%;
  }
`;

export const StyledShadow = styled.div<{ $right?: number; $visible?: boolean }>`
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  transition:
    opacity 0.3s ease-in-out,
    visibility 0.3s ease-in-out;
  position: absolute;
  top: 0;
  right: ${({ $right }) => ($right ? `${$right}px` : 0)};
  bottom: 0;
  width: 20px;
  box-shadow: inset -14px 0 20px -12px rgba(0, 0, 0, 0.2);
  z-index: 1;
  pointer-events: none;
`;
